import * as React from "react";

import PageHeader from "@replo/design-system/blocks/page-header/PageHeader";
import InlineAlert from "@v1/components/designSystem/alert/InlineAlert";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { Card } from "@v1/components/designSystem/shadcn/core/card";
import { DeprecatedTabs } from "@v1/components/designSystem/tabs/Tabs";
import AnalyticsContext from "@v1/features/analytics/AnalyticsContext";
import { ConditionallyBlurredComponent } from "@v1/features/analytics/ConditionallyBlurredComponent";
import { MetricsDocButton } from "@v1/features/analytics/MetricsDocButton";
import { useAnalyticsOnboardingOAuthLink } from "@v1/features/analytics/useAnalyticsOnboardingOAuthLink";
import { ConnectShopifyCallout } from "@v2/components/ConnectShopifyCallout";
import { Loader } from "@v2/components/Loader";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useAnalyticsStatus from "@v2/features/analytics/hooks/useAnalyticsStatus";
import AnnouncementMessage from "@v2/features/announcements/AnnouncementMessage";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useOpenModal } from "@v2/store/modalStore";
import { routes } from "@v2/utils/router";
import { Megaphone, Rocket } from "lucide-react";
import {
  generatePath,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

const ShopifyReconnectBanner: React.FC = () => {
  const { oauthLink, isLoading: isLoadingOauthLink } =
    useAnalyticsOnboardingOAuthLink();

  return (
    <InlineAlert
      variant="announcement"
      startEnhancer={<Megaphone size={16} className="text-fuchsia" />}
      endEnhancer={
        <DeprecatedButton
          variant="tertiary"
          size="sm"
          to={oauthLink ?? ""}
          isLoading={isLoadingOauthLink || !oauthLink}
        >
          Reconnect Store
        </DeprecatedButton>
      }
    >
      Seeing inaccurate or missing data? You may need to reconnect your Shopify
      Store.
    </InlineAlert>
  );
};

const ANALYTICS_TABS: {
  value: string;
  label: string;
}[] = [
  { value: "home", label: "Overview" },
  { value: "insights", label: "Insights" },
  { value: "overview", label: "Pages" },
];

export const ProjectAnalyticsDashboard: React.FC = () => {
  const { project } = useCurrentProjectContext();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const workspaceId = useCurrentWorkspaceId() ?? null;
  const logEvent = useLogAnalytics();

  const projectName = project?.name;

  const handleTabChange = (value: string) => {
    const route = exhaustiveSwitch({ type: value })({
      home: () => routes.shops.analytics.home,
      overview: () => routes.shops.analytics.overview,
      insights: () => routes.shops.analytics.insights,
    });
    const path = generatePath(route, {
      projectId: project?.id ?? "",
    });
    navigate(path);

    // Map the actual tab value to analytics event tab names
    const analyticsTabName = exhaustiveSwitch({ type: value })({
      home: () => "analytics_home" as const,
      overview: () => "analytics_overview" as const,
      insights: () => "analytics_insights" as const,
    });

    logEvent("dashboard.nav.click", {
      tab: analyticsTabName,
      userId: user?.id ?? null,
      workspaceId,
    });
  };

  return (
    <AnalyticsDashboard
      onTabChange={handleTabChange}
      pageHeaderTitle={projectName ? `${projectName} Analytics` : "Analytics"}
    />
  );
};

type AnalyticsDashboardProps = {
  pageHeaderTitle: string;
  onTabChange: (value: string) => void;
};
const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  pageHeaderTitle,
  onTabChange,
}) => {
  const { shouldBlurFullScreen, isLoading, dataDisplayType } =
    useAnalyticsStatus();
  const pathName = useLocation().pathname.split("/").pop();
  const isDeepDive = pathName === "deep-dive";

  const openModal = useOpenModal();

  const handleOpenBillingModal = () => {
    return openModal({
      type: "billingErrorModal",
      props: {
        billingPlanMessageKey: "billingPlan.analytics",
      },
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  const teaserComponent = exhaustiveSwitch({ type: dataDisplayType })({
    blurFullScreenRequireShopifyConnect: () => <ConnectShopifyTeaser />,
    blurFullScreenRequireUpgrade: () => {
      return (
        pathName && (
          <FakeDataTeaser
            path={pathName}
            onOpenBillingModal={handleOpenBillingModal}
          />
        )
      );
    },
  });

  return (
    <AnalyticsContext>
      <div className="w-full px-3 sm:px-6 flex flex-col overflow-hidden h-full">
        {!isDeepDive && (
          <PageHeader
            title={pageHeaderTitle}
            actions={<MetricsDocButton />}
            showDivider={false}
          >
            <AnnouncementMessage tags={["Insights"]} />
            {isFeatureEnabled("shopify-webpixel-reauth-banner") && (
              <ShopifyReconnectBanner />
            )}
          </PageHeader>
        )}
        <div className="flex flex-col gap-4 overflow-hidden flex-1 h-full">
          <div>
            {pathName !== "deep-dive" && (
              <DeprecatedTabs
                tabsOptions={ANALYTICS_TABS}
                value={pathName ?? "home"}
                onValueChange={onTabChange}
                size="base"
                defaultValue="home"
              />
            )}
          </div>
          <ConditionallyBlurredComponent
            shouldBlur={shouldBlurFullScreen}
            componentToBlur={
              dataDisplayType === "partialData" && pathName ? (
                <OutletWithPartialDataBanner
                  path={pathName}
                  onOpenBillingModal={handleOpenBillingModal}
                />
              ) : (
                <Outlet />
              )
            }
            visibleComponent={teaserComponent}
            visibleComponentClassName="mt-25"
          />
        </div>
      </div>
    </AnalyticsContext>
  );
};

const OutletWithPartialDataBanner: React.FC<{
  onOpenBillingModal: () => void;
  path: string;
}> = ({ onOpenBillingModal, path }) => {
  const { title, description } = exhaustiveSwitch({ type: path })({
    home: () => ({
      title: "See the Full Picture",
      description:
        "Upgrade to start tracking all data across all your store’s pages, products, and users.",
    }),
    insights: () => ({
      title: "We Found More Insights",
      description: "Upgrade to access all growth opportunities.",
    }),
    overview: () => ({
      title: "See How All Your Pages Are Performing",
      description:
        "Unlock full analytics to view all pages and advanced filters.",
    }),
    "deep-dive": () => ({
      title: "See the Full Picture",
      description: "See all Page analytics with a paid plan.",
    }),
  });

  return (
    <div className="relative h-full overflow-hidden">
      <div className="h-full overflow-auto">
        <Outlet />
      </div>
      <div className="absolute bottom-0 left-0 z-10 w-full">
        <div className="bg-default rounded-lg p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex flex-row gap-3">
            <div className="bg-sky-soft rounded-full p-2 flex items-center justify-center h-[48px] w-[48px]">
              {/* eslint-disable-next-line replo/no-tailwind-literal-color */}
              <Rocket size={18} className="text-indigo-600" />
            </div>
            <div className="flex flex-col text-white">
              <span className="header-h6">{title}</span>
              <span className="text-sm">{description}</span>
            </div>
          </div>
          <DeprecatedButton
            variant="secondary"
            size="base"
            startEnhancer={<Rocket size={14} />}
            onClick={onOpenBillingModal}
            layoutClassName="sm:ml-auto"
          >
            Unlock
          </DeprecatedButton>
        </div>
      </div>
    </div>
  );
};

const ConnectShopifyTeaser: React.FC = () => {
  const { project } = useCurrentProjectContext();
  if (!project) {
    return null;
  }

  return (
    <Card className="bg-white w-full sm:w-[350px]">
      <ConnectShopifyCallout
        cookieValue={{
          type: "analytics",
          projectId: project.id,
          workspaceId: project.ownerWorkspaceId,
        }}
      />
    </Card>
  );
};

const FakeDataTeaser: React.FC<{
  path: string;
  onOpenBillingModal: () => void;
}> = ({ path, onOpenBillingModal }) => {
  const { image, title, description } = exhaustiveSwitch({ type: path })({
    home: () => ({
      image: "/images/analytics/fake-data/fake-homepage.svg",
      title: "Measure Store Performance",
      description:
        "Upgrade to start tracking your store’s pages, products, and users.",
    }),
    insights: () => ({
      image: "/images/analytics/fake-data/fake-insights.svg",
      title: "Find Opportunities to Act on",
      description:
        "Even while you sleep, Insights is working to spot problems and unlock wins.",
    }),
    overview: () => ({
      image: "/images/analytics/fake-data/fake-pages.svg",
      title: "Know Whats Happens on Every Page",
      description:
        "See which pages drive sales, where you lose visitors, and how to grow faster.",
    }),
  });

  return (
    <Card className="bg-white">
      <div className="space-y-4 items-center flex flex-col">
        <img src={image} alt="Analytics Preview Image" />
        <div className="flex flex-col gap-1 items-center w-full sm:w-[530px] px-3">
          <span className="header-h4">{title}</span>
          <span className="text-sm text-muted text-center">{description}</span>
        </div>
        <DeprecatedButton
          size="base"
          variant="primary"
          isLoading={false}
          onClick={onOpenBillingModal}
        >
          Unlock Analytics
        </DeprecatedButton>
      </div>
    </Card>
  );
};
